import { action, flow, makeObservable, observable } from 'mobx';
import {
  ConnectionService,
  TakerConnectService,
  TakerConnectServices,
  TakerConnectValidationErrors,
  connectionServiceCast,
} from '../types/takerConnect';
import { modalStore } from '~/mobx/modalStore';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';

const mapTakerConnectServices = (servicesList: any): TakerConnectServices => {
  return servicesList.reduce(
    (servicesMap: TakerConnectServices, service: any) => servicesMap.set(service.title, connectionServiceCast(service)),
    new Map()
  );
};

class TakerConnectStore {
  constructor() {
    makeObservable(this);
  }

  @observable services: TakerConnectServices = new Map();
  @observable isDataEdited = false;
  @observable loadingStatus = LoadingStatus.Init;

  @observable validationErrors: TakerConnectValidationErrors = { senderName: null, userName: null };

  @action public init = () => {
    this.loadingStatus = LoadingStatus.Init;
    this.isDataEdited = false;

    this.fetchTakerConnectData();
  };

  @action changeServiceData = (service: ConnectionService, data: Partial<TakerConnectService>) => {
    this.services.set(service, { ...this.services.get(service)!, ...data });
    this.isDataEdited = true;
  };

  @action private fetchTakerConnectData = flow(function* (this: TakerConnectStore) {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const data = yield client.get('v3/integrations/connect');
      this.services = mapTakerConnectServices(data.services);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'TakerConnectStore fetchTakerConnectData' });
    }
  });

  @action updateTakerConnectData = flow(function* (this: TakerConnectStore) {
    try {
      this.loadingStatus = LoadingStatus.Refreshing;

      const response = yield client.post('v3/integrations/connect', this._getApiParams());

      if (response.status !== undefined && !response.status) {
        modalStore.openErrorModal({ message: response.msg });
      } else {
        this.services = mapTakerConnectServices(response.services);
      }

      this.loadingStatus = LoadingStatus.Success;
      this.isDataEdited = false;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'TakerConnectStore updateTakerConnectData', showModal: true });
    }
  });

  @action validateSenderName = (senderName: string) => {
    this.validationErrors.senderName = !senderName ? 'required_field_error' : null;
  };

  @action validateUserName = (userName: string) => {
    this.validationErrors.userName = !userName ? 'required_field_error' : null;
  };

  @action reset = () => {
    this.services = new Map();
    this.validationErrors = { senderName: null, userName: null };
    this.loadingStatus = LoadingStatus.Init;
  };

  runValidation = () => {
    const { status: msegatStatus, senderName: msegatSenderName, userName: msegatUserName } = this.services.get(ConnectionService.Msegat)!;

    if (msegatStatus) {
      this.validateSenderName(msegatSenderName);
      this.validateUserName(msegatUserName);
    }
  };

  private _getApiParams = () => {
    const unifonic = this.services.get(ConnectionService.Unifonic)!;
    const msegat = this.services.get(ConnectionService.Msegat)!;

    return {
      service: {
        unifonic: {
          status: unifonic.status,
          priority: Number(unifonic.priority) || 1,
          api_key: unifonic.apiKey,
          sender_name: unifonic.senderName,
          api_url: unifonic.apiUrl,
        },
        msegat: {
          status: msegat.status,
          priority: Number(msegat.priority) || 1,
          api_key: msegat.apiKey,
          sender_name: msegat.senderName,
          user_name: msegat.userName,
          api_url: msegat.apiUrl,
        },
      },
    };
  };
}

const takerConnectStore = new TakerConnectStore();

export default takerConnectStore;
